/**
 * Client-side-only draft persistence — "form state survives accidental close and browser back"
 * (PRD §1.5.5), without a new public write endpoint.
 *
 * ## Why this is `localStorage`, not a server round trip
 *
 * WP-18's build log records the alternative considered and rejected: a server-side autosave
 * endpoint that persists a half-filled form before the donor has pressed Continue. Rejected
 * because it is unsubmitted data leaving the browser before the donor asked it to, it is a new
 * unauthenticated public write endpoint (`10-SECURITY-HARDENING.md`'s `Rest\*` review gate names
 * exactly this class of addition), and it solves a problem `localStorage` already solves without
 * either cost: a closed tab, a browser-back, and a reopened bookmark all read from the same
 * origin's storage with no network involved. `Frontend\Checkout\SessionRepository` — the one
 * thing this package writes server-side — records what a session *submitted*, at `intent()`,
 * never what it is still typing.
 *
 * The trade this accepts, named rather than hidden: a donor who switches browsers or devices
 * mid-form, or who clears site data, loses the draft. No worse than most checkout forms, and
 * strictly better on the dimension that matters most here — nothing unsubmitted ever reaches a
 * server this plugin does not control the donor's own browser to be.
 *
 * Every read and write is wrapped, per the artifact-storage discipline this codebase already
 * applies elsewhere: a private window, a full storage quota, or a browser blocking site data
 * must degrade to "no draft found," never to a thrown error that stops the checkout from
 * rendering at all.
 */

const PREFIX = 'anudan_checkout_draft_';

/**
 * Returns the storage key for one fund's draft.
 *
 * @param {number} fundId
 * @return {string}
 */
function key( fundId ) {
	return PREFIX + String( fundId );
}

/**
 * Saves a draft. Silently does nothing if storage is unavailable.
 *
 * @param {number} fundId
 * @param {object} draft
 * @return {void}
 */
export function saveDraft( fundId, draft ) {
	try {
		window.localStorage.setItem( key( fundId ), JSON.stringify( { ...draft, savedAt: Date.now() } ) );
	} catch ( error ) {
		// Storage unavailable or full. The donor keeps typing; only the resume feature is lost.
	}
}

/**
 * Loads a draft, or null when there is none, it is malformed, or storage is unavailable.
 *
 * @param {number} fundId
 * @return {object|null}
 */
export function loadDraft( fundId ) {
	try {
		const raw = window.localStorage.getItem( key( fundId ) );

		if ( null === raw ) {
			return null;
		}

		const parsed = JSON.parse( raw );

		return 'object' === typeof parsed && null !== parsed ? parsed : null;
	} catch ( error ) {
		return null;
	}
}

/**
 * Clears a fund's draft — called once a donation is actually created, since the form state it
 * describes no longer needs resuming.
 *
 * @param {number} fundId
 * @return {void}
 */
export function clearDraft( fundId ) {
	try {
		window.localStorage.removeItem( key( fundId ) );
	} catch ( error ) {
		// Nothing to do; the draft will simply be overwritten or expire from disuse.
	}
}
